
export default function Cookies(){
  return (
    <div className="container py-12 prose max-w-4xl">
      <h1>Cookie Policy</h1>
      <p className="text-sm text-gray-600">Effective Date: January 1, 2025 | Last Updated: January 1, 2025</p>

      <h2>1. What Are Cookies?</h2>
      <p>
        Cookies are small text files placed on your device (computer, smartphone, tablet) when you visit
        a website. Cookies help websites remember information about your visit, making your next visit
        easier and the site more useful to you.
      </p>
      <p><strong>Similar technologies we use:</strong></p>
      <ul>
        <li><strong>Web beacons (pixels):</strong> Tiny graphics that track page views and email opens</li>
        <li><strong>Local storage:</strong> Browser storage for persistent data</li>
        <li><strong>Session storage:</strong> Temporary storage cleared when browser closes</li>
      </ul>

      <h2>2. Types of Cookies We Use</h2>

      <h3>2.1 Strictly Necessary Cookies (Cannot Opt Out)</h3>
      <p>
        These cookies are essential for our website to function properly. They enable core functionality
        such as security, network management, and accessibility.
      </p>
      <p><strong>Examples:</strong></p>
      <ul>
        <li>Authentication cookies (keep you logged in)</li>
        <li>Security cookies (protect against fraud)</li>
        <li>Cookie consent preferences</li>
      </ul>
      <p className="text-sm italic">Legal Basis: Legitimate interest / Contractual necessity</p>

      <h3>2.2 Analytics/Performance Cookies (Can Opt Out)</h3>
      <p>
        These cookies help us understand how visitors use our website so we can improve it. We use
        Google Analytics 4 to collect anonymous usage data.
      </p>
      <p><strong>Data collected:</strong></p>
      <ul>
        <li>Pages visited and time spent on pages</li>
        <li>Traffic source (how you found us)</li>
        <li>Device type, browser, and operating system</li>
        <li>Geographic location (city/state level only)</li>
        <li>Navigation paths and user flow</li>
      </ul>
      <p><strong>Cookies used:</strong></p>
      <ul>
        <li><code>_ga</code> - Distinguishes unique users (expires in 2 years)</li>
        <li><code>_ga_[ID]</code> - Stores session information (expires in 2 years)</li>
        <li><code>_gid</code> - Distinguishes users (expires in 24 hours)</li>
      </ul>
      <p>
        We use IP anonymization to protect your privacy. Google Analytics does not track personally
        identifiable information.
      </p>
      <p>
        <strong>Opt out:</strong> <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
          Install Google Analytics Opt-out Browser Add-on
        </a>
      </p>
      <p className="text-sm italic">Legal Basis: Consent (GDPR) / Legitimate interest with opt-out (CCPA)</p>

      <h3>2.3 Advertising/Marketing Cookies (Can Opt Out)</h3>
      <p>
        These cookies are used to deliver relevant ads and measure campaign effectiveness. They may be
        set by us or third-party advertising partners.
      </p>
      <p><strong>Purpose:</strong></p>
      <ul>
        <li>Show you relevant ads on other websites (retargeting)</li>
        <li>Measure ad campaign performance</li>
        <li>Build audience segments for targeting</li>
        <li>Prevent showing you the same ad repeatedly</li>
      </ul>
      <p><strong>Third-party providers:</strong></p>
      <ul>
        <li><strong>Google Ads:</strong> Conversion tracking and retargeting</li>
        <li><strong>Facebook Pixel:</strong> Track visits and conversions</li>
        <li><strong>LinkedIn Insight Tag:</strong> B2B ad analytics</li>
      </ul>
      <p className="font-bold">
        IMPORTANT: This may constitute "sharing" for cross-context behavioral advertising under California
        law (CCPA/CPRA). California residents can opt out below.
      </p>
      <p><strong>Opt-out options:</strong></p>
      <ul>
        <li><a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Google Ads Settings</a></li>
        <li><a href="https://www.facebook.com/settings?tab=ads" target="_blank" rel="noopener noreferrer">Facebook Ad Preferences</a></li>
        <li><a href="https://www.linkedin.com/psettings/guest-controls/retargeting-opt-out" target="_blank" rel="noopener noreferrer">LinkedIn Retargeting Opt-Out</a></li>
        <li><a href="https://optout.aboutads.info" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance Opt-Out</a></li>
        <li><a href="https://optout.networkadvertising.org" target="_blank" rel="noopener noreferrer">Network Advertising Initiative Opt-Out</a></li>
      </ul>
      <p className="text-sm italic">Legal Basis: Consent</p>

      <h2>3. First-Party vs. Third-Party Cookies</h2>
      <p><strong>First-party cookies:</strong> Set by AdKarrot LLC (adkarrot.com domain)</p>
      <ul>
        <li>Session management</li>
        <li>Website functionality</li>
        <li>User preferences</li>
      </ul>
      <p><strong>Third-party cookies:</strong> Set by external services</p>
      <ul>
        <li>Google Analytics (_ga, _gid)</li>
        <li>Google Ads (conversion tracking)</li>
        <li>Facebook Pixel (_fbp)</li>
        <li>LinkedIn Insight Tag</li>
      </ul>
      <p>Third-party cookies are governed by those providers' privacy policies.</p>

      <h2>4. How to Manage Cookies</h2>

      <h3>4.1 Browser Settings</h3>
      <p>All major browsers allow you to control cookies:</p>
      <ul>
        <li><strong>Google Chrome:</strong> Settings → Privacy and Security → Cookies and other site data
          (<a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Learn more</a>)</li>
        <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data
          (<a href="https://support.mozilla.org/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer">Learn more</a>)</li>
        <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data
          (<a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer">Learn more</a>)</li>
        <li><strong>Microsoft Edge:</strong> Settings → Cookies and site permissions
          (<a href="https://support.microsoft.com/microsoft-edge/delete-cookies" target="_blank" rel="noopener noreferrer">Learn more</a>)</li>
      </ul>

      <h3>4.2 Mobile Browsers</h3>
      <ul>
        <li><strong>iOS Safari:</strong> Settings → Safari → Block All Cookies</li>
        <li><strong>Android Chrome:</strong> Chrome app → Settings → Site Settings → Cookies</li>
      </ul>

      <h3>4.3 Consequences of Disabling Cookies</h3>
      <p>If you disable cookies:</p>
      <ul>
        <li>Some website features may not function properly</li>
        <li>We won't remember your preferences</li>
        <li>You may see less relevant advertising</li>
        <li>Analytics will be limited (we can't improve user experience as effectively)</li>
      </ul>

      <h2>5. Do Not Track Signals</h2>
      <p>
        Some browsers offer a "Do Not Track" (DNT) setting. Currently, there is no universal industry
        standard for how to respond to DNT signals.
      </p>
      <p><strong>Our approach:</strong></p>
      <ul>
        <li>We honor Global Privacy Control (GPC) signals as opt-outs under CCPA</li>
        <li>We do not currently respond to DNT browser settings</li>
        <li>You can use the opt-out methods described above to control tracking</li>
      </ul>

      <h3>5.1 California Residents - Do Not Sell or Share</h3>
      <p>
        If you are a California resident, you can opt out of the "sale" or "sharing" of your personal
        information for advertising purposes:
      </p>
      <p>
        <a href="mailto:privacy@adkarrot.com?subject=Do%20Not%20Sell%20or%20Share%20My%20Information"
           className="font-bold text-blue-600 hover:underline">
          Do Not Sell or Share My Personal Information
        </a>
      </p>

      <h2>6. Cookie Lifespan</h2>
      <p><strong>Session cookies:</strong> Deleted when you close your browser</p>
      <p><strong>Persistent cookies:</strong> Remain until expiration or manual deletion</p>
      <p>Our cookie durations:</p>
      <ul>
        <li>Strictly necessary: Up to 1 year</li>
        <li>Analytics: Up to 26 months (Google Analytics default)</li>
        <li>Advertising: Up to 13 months</li>
      </ul>

      <h2>7. Updates to This Cookie Policy</h2>
      <p>
        We may update this Cookie Policy to reflect new cookies, tracking technologies, or legal
        requirements. The "Last Updated" date at the top indicates when the policy was last revised.
        We recommend reviewing this policy periodically.
      </p>

      <h2>8. Contact Us About Cookies</h2>
      <p>Questions about our use of cookies?</p>
      <p>
        Email: <a href="mailto:privacy@adkarrot.com">privacy@adkarrot.com</a><br/>
        Phone: 319-343-6094<br/>
        Mail: AdKarrot LLC, Attn: Cookie Policy Inquiries, 309 Court Avenue Suite 855, Des Moines, IA 50309
      </p>
      <p>
        For privacy rights requests (access, deletion, opt-out), see our <a href="/legal/privacy">Privacy Policy</a>.
      </p>

      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded">
        <p className="text-sm font-semibold">Quick Links:</p>
        <ul className="text-sm mt-2 space-y-1">
          <li><a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            Google Analytics Opt-Out
          </a></li>
          <li><a href="https://optout.aboutads.info" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            Ad Industry Opt-Out (DAA)
          </a></li>
          <li><a href="https://optout.networkadvertising.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            Ad Industry Opt-Out (NAI)
          </a></li>
        </ul>
      </div>
    </div>
  )
}
